import { ComponentType } from '../../typegen/component-type';
import { KeyValue } from '../../typegen/key-value';
import { KioskState } from '../../typegen/kiosk-state';
import { LedDeviceMapping } from '../../typegen/led-device-mapping';
import { LedHighlightDevice } from '../../typegen/led-highlight-device';
import { LookupType } from '../../typegen/lookup-type';
import { LedChannel } from './LedBoardDevice';

export const ALERT_COLOR = 'FF0000';
export const AVAILABLE_COLOR = '00FF00';

const NEEDS_ATTENTION_STATES: ReadonlySet<KioskState> = new Set([
	KioskState.Maintenance,
	KioskState.Unavailable,
	KioskState.OutOfService,
	KioskState.CashBoxAlarm,
]);

export const DEVICE_PAGE_COMPONENT_TYPES: Record<LedHighlightDevice, readonly ComponentType[]> = {
	[LedHighlightDevice.CashAcceptor]: [ComponentType.CashPayment],
	[LedHighlightDevice.CardReader]: [
		ComponentType.CardPaymentMagTek,
		ComponentType.CardPaymentEMV,
		ComponentType.CardPaymentEmvMock,
		ComponentType.CardPaymentToken,
	],
	[LedHighlightDevice.CheckReader]: [ComponentType.CheckPayment, ComponentType.CheckPaymentWithManualEntry],
	[LedHighlightDevice.ReceiptPrinter]: [ComponentType.ReceiptPrint],
	[LedHighlightDevice.BarcodeScanner]: [],
};

export function isNeedsAttentionState(state: KioskState | undefined): boolean {
	return state !== undefined && NEEDS_ATTENTION_STATES.has(state);
}

export type BaseChannelPattern = { kind: 'solid'; channel: LedChannel; color: string };

export function resolveBaseChannelPattern(params: {
	channel: LedChannel;
	kioskState: KioskState | undefined;
	sessionActive: boolean;
	brandingColors: string[];
	brandingColorIndex: number;
}): BaseChannelPattern | null {
	const { channel, kioskState, sessionActive, brandingColors, brandingColorIndex } = params;

	if (isNeedsAttentionState(kioskState)) {
		return { kind: 'solid', channel, color: ALERT_COLOR };
	}

	if (!sessionActive) {
		if (brandingColors.length === 0) return null;
		const index = brandingColorIndex % brandingColors.length;
		return { kind: 'solid', channel, color: brandingColors[index] };
	}

	return { kind: 'solid', channel, color: AVAILABLE_COLOR };
}

export function isScanBarcodeLookupActive(pageParams: KeyValue[] | undefined): boolean {
	const lookupType = Number(pageParams?.find((param) => param.key === 'lookupType')?.value);
	if (lookupType === LookupType.Barcode) return true;
	if (lookupType === LookupType.BarcodeWithLastName) {
		return !pageParams?.find((param) => param.key === 'scannedAccountNumber')?.value;
	}
	return false;
}

export function isBarcodeScannerAcceptingScans(params: {
	pageIds: string[];
	currentPageId: string | undefined;
	lookupAccountPageId: string;
	useGlobalBarcodeScanner: boolean;
	kioskNeedsAttention: boolean;
	pageParams: KeyValue[] | undefined;
}): boolean {
	const { pageIds, currentPageId, lookupAccountPageId, useGlobalBarcodeScanner, kioskNeedsAttention, pageParams } = params;
	if (kioskNeedsAttention || !currentPageId) return false;

	if (useGlobalBarcodeScanner) {
		const currentIndex = pageIds.indexOf(currentPageId);
		const lookupIndex = pageIds.indexOf(lookupAccountPageId);
		if (currentIndex === -1 || lookupIndex === -1 || currentIndex > lookupIndex) return false;
		if (currentIndex < lookupIndex) return true;
		return isScanBarcodeLookupActive(pageParams);
	}

	if (currentPageId !== lookupAccountPageId) return false;
	return isScanBarcodeLookupActive(pageParams);
}

export function resolveActiveDeviceMapping(params: {
	deviceMappings: LedDeviceMapping[];
	componentTypesOnPage: ComponentType[];
	barcodeScannerAccepting: boolean;
	sessionActive: boolean;
}): LedDeviceMapping | undefined {
	const { deviceMappings, componentTypesOnPage, barcodeScannerAccepting, sessionActive } = params;

	return deviceMappings.find((mapping) => {
		if (mapping.device === LedHighlightDevice.BarcodeScanner) {
			return barcodeScannerAccepting;
		}
		if (!sessionActive) return false;
		return DEVICE_PAGE_COMPONENT_TYPES[mapping.device].some((type) => componentTypesOnPage.includes(type));
	});
}
